import Box, { BoxProps } from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import {
  CSSProperties,
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import {
  FixedSizeGrid,
  FixedSizeList,
  GridChildComponentProps,
  GridOnScrollProps,
  ListChildComponentProps,
} from 'react-window';

import {
  HEADER_PANEL_Z_INDEX,
  HeaderPanel,
  OverflowAwareText,
} from '~components/Order/ordersDispatchStyledComponents';
import Tabs, { TabProps } from '~components/Tabs/Tabs';
import { JobAssignmentType } from '~constants/enums';
import { FeatureFlags } from '~constants/featureFlags';
import { DriverBasic } from '~hooks/useDrivers/models';
import useDriverSchedulerFetch from '~pages/Dispatch/hooks/useDriverSchedulerFetch';
import DriverSchedulerStore from '~store/DriverSchedulerStore';
import { useStores } from '~store/RootStore';
import { Nullable } from '~types/Nullable';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';
import { hexToRgba } from '~utils/utilFunctions';

import { AssigneeCard } from './AssigneeCard';
import {
  DUMMY_COLUMNS_SIZE,
  INCREMENTS_PER_HOUR,
  INFINITE_SCROLL_THRESHOLD_PERCENTAGE,
  JOB_COLUMN_WIDTH,
  OVERSCAN_COLUMN_COUNT,
  STICKY_HEADER_HEIGHT,
} from './constants';
import { GRID_ITEM_WIDTH, HOURS, ROW_HEIGHT } from './constants';
import { DriverSearch } from './DriverSearch';
import { DroppableCell } from './DroppableCell';

const assigneeTypeTabs: TabProps<JobAssignmentType>[] = [
  {
    label: t('dispatch.job.assignment_popover.driver'),
    value: JobAssignmentType.DRIVER,
  },
  {
    label: t('dispatch.job.assignment_popover.vendor'),
    value: JobAssignmentType.VENDOR,
  },
];

const getJobCountForAssignee = (assigneeId: string, store: DriverSchedulerStore) => {
  return Object.entries(store.jobStacks)
    .filter(([slotKey]) => slotKey.startsWith(`${assigneeId}:`))
    .reduce((jobCount, [, stackedJobs]) => jobCount + stackedJobs.length, 0);
};

const StickyRow = ({ width }: { width: Nullable<string | number> }) => (
  <Box
    sx={{
      position: 'sticky',
      top: 0,
      zIndex: HEADER_PANEL_Z_INDEX + 1,
      bgcolor: 'background.paper',
      display: 'flex',
      width: width,
    }}
  >
    {HOURS.map((hour, index) => (
      <HeaderPanel
        key={index}
        sx={{
          width: GRID_ITEM_WIDTH * INCREMENTS_PER_HOUR + 1,
          borderBottom: (theme) => `1px solid ${theme.brandV2.colors.treadGray7}`,
          borderRight: (theme) => `1px solid ${theme.brandV2.colors.treadGray7}`,
          textAlign: 'center',
          paddingTop: 1,
          height: STICKY_HEADER_HEIGHT,
        }}
      >
        <Typography variant="subtitle2">{hour}</Typography>
      </HeaderPanel>
    ))}
  </Box>
);

interface InnerElementProps extends BoxProps {
  children: ReactNode;
  style: CSSProperties;
}

const innerElementType = forwardRef<HTMLDivElement, InnerElementProps>(
  ({ children, ...rest }, ref) => (
    <Box ref={ref} {...rest}>
      <StickyRow width={rest.style.width} />
      {children}
    </Box>
  ),
);

innerElementType.displayName = 'innerElementType';

type AssigneeTimeSlotCellProps = GridChildComponentProps<
  DriverSchedulerStore['filteredAssignees']
>;

const AssigneeTimeSlotCell = observer(
  ({ columnIndex, data: assignees, rowIndex, style }: AssigneeTimeSlotCellProps) => {
    const styleBelowStickyHeader = useMemo(
      () => ({ ...style, top: (Number(style.top) || 0) + STICKY_HEADER_HEIGHT }),
      [style],
    );

    return (
      <DroppableCell
        assigneeId={assignees[rowIndex]?.id}
        columnIndex={columnIndex}
        style={styleBelowStickyHeader}
      />
    );
  },
);

type AssigneeColumnRowProps = ListChildComponentProps<
  DriverSchedulerStore['filteredAssignees']
>;

const AssigneeColumnRow = observer(
  ({ data: assignees, index, style }: AssigneeColumnRowProps) => {
    const { driverSchedulerStore } = useStores();
    const isLoadingMoreRow =
      index === assignees.length && driverSchedulerStore.isFetchingMoreAssignees;

    if (isLoadingMoreRow) {
      return (
        <Box
          style={style}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      );
    }

    const assignee = assignees[index];
    if (!assignee) return null;

    const jobCount =
      assignee instanceof DriverBasic
        ? (assignee.companyDayDriverStat?.jobsCount ?? 0)
        : getJobCountForAssignee(assignee.id, driverSchedulerStore);

    return (
      <AssigneeCard
        key={assignee.id}
        assignee={assignee}
        style={style}
        jobCount={jobCount}
      />
    );
  },
);

export const TimeSlotCellGrid = observer(() => {
  const { driverSchedulerStore, userStore } = useStores();
  const theme = useTheme();
  const { fetchMoreAssignees } = useDriverSchedulerFetch();
  const driverColumnRef = useRef<FixedSizeList>(null);
  const hoursGridRef = useRef<FixedSizeGrid>(null);
  const driversColumnScrollTop = useRef(0);
  const hoursGridScrollTop = useRef(0);

  const assignees = driverSchedulerStore.filteredAssignees;

  const switchAssigneeTabsOrderFeatureFlagEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.switchAssigneeTabsOrder,
  });

  const sortedAssigneeTypeTabs = useMemo(() => {
    if (switchAssigneeTabsOrderFeatureFlagEnabled) {
      return [...assigneeTypeTabs].reverse();
    }

    return assigneeTypeTabs;
  }, [switchAssigneeTabsOrderFeatureFlagEnabled]);

  const onDriverColumnScroll = useCallback(
    ({ scrollOffset }: { scrollOffset: number }) => {
      driversColumnScrollTop.current = scrollOffset;
      const shouldScrollHoursGrid =
        hoursGridRef.current && hoursGridScrollTop.current !== scrollOffset;

      if (shouldScrollHoursGrid) {
        hoursGridRef.current.scrollTo({
          scrollTop: scrollOffset,
        });
      }
    },
    [],
  );

  // Separate handler for checking infinite scroll
  const checkInfiniteScroll = useCallback(
    (scrollOffset: number, listHeight: number) => {
      if (assignees.length > 0) {
        const totalHeight = assignees.length * ROW_HEIGHT;
        const scrollPercentage = (scrollOffset + listHeight) / totalHeight;
        const companyId = getEffectiveUserCompanyId(userStore);

        // Trigger load more when scrolled past 90% of the list
        if (
          scrollPercentage > INFINITE_SCROLL_THRESHOLD_PERCENTAGE &&
          driverSchedulerStore.hasMoreAssignees &&
          !driverSchedulerStore.isFetchingMoreAssignees &&
          companyId
        ) {
          void Promise.resolve(fetchMoreAssignees(companyId)).catch(() => {
            // Error is handled by the hook.
          });
        }
      }
    },
    [
      assignees.length,
      fetchMoreAssignees,
      userStore,
      driverSchedulerStore.hasMoreAssignees,
      driverSchedulerStore.isFetchingMoreAssignees,
    ],
  );

  const onHoursGridScroll = useCallback(({ scrollTop }: GridOnScrollProps) => {
    hoursGridScrollTop.current = scrollTop;
    const shouldScrollDriversColumn =
      driverColumnRef.current && driversColumnScrollTop.current !== scrollTop;

    if (shouldScrollDriversColumn) {
      driverColumnRef.current.scrollTo(scrollTop);
    }
  }, []);

  useEffect(() => {
    if (switchAssigneeTabsOrderFeatureFlagEnabled) {
      driverSchedulerStore.setAssigneesFilters({
        assigneeType: JobAssignmentType.VENDOR,
      });
    }
  }, [switchAssigneeTabsOrderFeatureFlagEnabled]);

  return (
    <AutoSizer>
      {({ height, width }) => (
        <Box
          sx={{
            display: 'flex',
          }}
        >
          {/* Sticky driver column */}
          <Box
            sx={{
              zIndex: HEADER_PANEL_Z_INDEX + 1,
              height: height,
            }}
          >
            <HeaderPanel
              sx={{
                position: 'sticky',
                backgroundColor: theme.brandV2.colors.treadGray9,
                width: `${JOB_COLUMN_WIDTH}px`,
                height: `${STICKY_HEADER_HEIGHT}px`,
                pl: 2,
                pr: 2,
                borderRight: `1px solid ${theme.brandV2.colors.treadGray7}`,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <OverflowAwareText fontWeight={600}>
                  {t('dispatch.drivers.driver_and_vendor')}
                </OverflowAwareText>

                <Tabs
                  onChange={(assigneeType) => {
                    driverSchedulerStore.setAssigneesFilters({ assigneeType });
                  }}
                  selected={driverSchedulerStore.assigneesFilters.assigneeType}
                  tabs={sortedAssigneeTypeTabs}
                  tabSx={{ '&.MuiTab-root': { minWidth: 'auto' } }}
                  sx={{ mt: -0.5 }}
                />

                <DriverSearch />
              </Box>
            </HeaderPanel>

            {/* Drivers list */}
            <Box
              sx={{
                bgcolor: hexToRgba(theme.brandV2.colors.treadGray8, 1),
              }}
            >
              <FixedSizeList
                ref={driverColumnRef}
                itemCount={
                  assignees.length +
                  (driverSchedulerStore.isFetchingMoreAssignees ? 1 : 0)
                }
                itemData={assignees}
                itemSize={ROW_HEIGHT}
                width={JOB_COLUMN_WIDTH}
                height={height - STICKY_HEADER_HEIGHT} // Adjusts for sticky header height and hours grid scrollbar
                onScroll={({ scrollOffset, scrollUpdateWasRequested }) => {
                  onDriverColumnScroll({ scrollOffset });
                  if (!scrollUpdateWasRequested) {
                    checkInfiniteScroll(scrollOffset, height - STICKY_HEADER_HEIGHT);
                  }
                }}
                style={{
                  overflowX: 'hidden',
                  overflowY: 'auto',
                  scrollbarWidth: 'none',
                }}
              >
                {AssigneeColumnRow}
              </FixedSizeList>
            </Box>
          </Box>

          {/* Main grid with sticky header */}
          <Box
            sx={{
              height: height,
              position: 'relative',
              width: width + DUMMY_COLUMNS_SIZE,
            }}
          >
            <Box
              sx={{
                bottom: 0,
                left: DUMMY_COLUMNS_SIZE * -1,
                position: 'absolute',
                right: DUMMY_COLUMNS_SIZE * -1,
                top: 0,
              }}
            >
              <FixedSizeGrid
                ref={hoursGridRef}
                columnCount={HOURS.length * INCREMENTS_PER_HOUR}
                columnWidth={GRID_ITEM_WIDTH}
                height={height}
                innerElementType={innerElementType}
                itemData={assignees}
                rowCount={assignees.length}
                rowHeight={ROW_HEIGHT}
                width={width - JOB_COLUMN_WIDTH + DUMMY_COLUMNS_SIZE}
                onScroll={(props) => {
                  onHoursGridScroll(props);
                  // Check for infinite scroll when scrolling the hours grid
                  if (!props.scrollUpdateWasRequested) {
                    checkInfiniteScroll(props.scrollTop, height - STICKY_HEADER_HEIGHT);
                  }
                }}
                overscanColumnCount={OVERSCAN_COLUMN_COUNT}
              >
                {AssigneeTimeSlotCell}
              </FixedSizeGrid>
            </Box>
          </Box>
        </Box>
      )}
    </AutoSizer>
  );
});
